// Widget catalog — single source of truth for what's available on the
// customizable dashboard. Each widget is { id, title, icon, size, role,
// Render }. Layout state on the page is just an ordered array of these
// ids; Dashboard.js looks each one up here and renders it.
import {
  CheckSquare, Users, DollarSign, Trophy, Activity, Phone, AlertTriangle,
  TrendingUp, Calendar, BarChart3, Zap, PieChart, ListChecks
} from 'lucide-react';
import {
  StatsOverviewWidget,
  MyOpenTasksWidget,
  DueTodayWidget,
  OverdueTasksWidget,
  NewLeadsThisWeekWidget,
  PipelineValueWidget,
  WonThisMonthWidget,
  TopRepWidget,
  RecentActivityWidget,
  UpcomingMeetingsWidget,
  ConversionFunnelWidget,
  MyTasksFullWidget,
  QuickActionsWidget,
  StatusDistributionWidget,
  SourceDistributionWidget,
  RecentActivitiesFullWidget,
} from './widgetComponents';

// Widget sizes:
//   sm — fits 4 across on lg screens (count cards)
//   md — fits 2 across on lg screens (list cards)
//   lg — full width (charts, tables)
export const WIDGETS = {
  stats_overview: {
    id: 'stats_overview',
    title: 'Stats overview',
    description: 'The four classic cards: Total Leads, New Leads, High Priority, Total Value.',
    icon: BarChart3,
    size: 'lg',
    role: 'any',
    Render: StatsOverviewWidget,
  },
  my_open_tasks: {
    id: 'my_open_tasks',
    title: 'My open tasks',
    description: 'Tasks assigned to you that are still open.',
    icon: CheckSquare,
    size: 'sm',
    role: 'any',
    Render: MyOpenTasksWidget,
  },
  due_today: {
    id: 'due_today',
    title: 'Due today',
    description: 'Your tasks due before midnight.',
    icon: Calendar,
    size: 'sm',
    role: 'any',
    Render: DueTodayWidget,
  },
  overdue_tasks: {
    id: 'overdue_tasks',
    title: 'Overdue tasks',
    description: 'Past-due open tasks needing attention.',
    icon: AlertTriangle,
    size: 'sm',
    role: 'any',
    Render: OverdueTasksWidget,
  },
  new_leads_week: {
    id: 'new_leads_week',
    title: 'New leads (week)',
    description: 'Leads created in the last 7 days.',
    icon: Users,
    size: 'sm',
    role: 'any',
    Render: NewLeadsThisWeekWidget,
  },
  pipeline_value: {
    id: 'pipeline_value',
    title: 'Pipeline value',
    description: 'Sum of estimated values across open leads.',
    icon: DollarSign,
    size: 'sm',
    role: 'any',
    Render: PipelineValueWidget,
  },
  won_month: {
    id: 'won_month',
    title: 'Won this month',
    description: 'Leads converted in the current calendar month.',
    icon: TrendingUp,
    size: 'sm',
    role: 'any',
    Render: WonThisMonthWidget,
  },
  top_rep: {
    id: 'top_rep',
    title: 'Top performer (90d)',
    description: 'Rep with the highest revenue in the last 90 days.',
    icon: Trophy,
    size: 'md',
    role: 'admin',
    Render: TopRepWidget,
  },
  recent_activity: {
    id: 'recent_activity',
    title: 'Recent activity',
    description: 'Latest notes and conversation events.',
    icon: Activity,
    size: 'md',
    role: 'any',
    Render: RecentActivityWidget,
  },
  upcoming_meetings: {
    id: 'upcoming_meetings',
    title: 'Upcoming meetings',
    description: 'Next scheduled call / meeting tasks.',
    icon: Phone,
    size: 'md',
    role: 'any',
    Render: UpcomingMeetingsWidget,
  },
  conversion_funnel: {
    id: 'conversion_funnel',
    title: 'Conversion funnel',
    description: 'How leads spread across pipeline stages right now.',
    icon: TrendingUp,
    size: 'lg',
    // Hits /reports/sales which is gated by view_reports — reps don't get
    // it by default, and the funnel shows org-wide aggregates that aren't
    // meaningful for an individual rep anyway. Admin-only.
    role: 'admin',
    Render: ConversionFunnelWidget,
  },
  // ---- Migrated from former static dashboard sections --------------------
  my_tasks_full: {
    id: 'my_tasks_full',
    title: 'My Tasks (full panel)',
    description: 'Overdue / today / this-week list of your open tasks with inline actions.',
    icon: ListChecks,
    size: 'lg',
    role: 'any',
    Render: MyTasksFullWidget,
  },
  quick_actions: {
    id: 'quick_actions',
    title: 'Quick actions',
    description: 'Shortcut buttons for adding a lead, uploading a card, viewing leads.',
    icon: Zap,
    size: 'lg',
    role: 'any',
    Render: QuickActionsWidget,
  },
  status_distribution: {
    id: 'status_distribution',
    title: 'Status distribution',
    description: 'How your leads are split across statuses (new / contacted / won / lost).',
    icon: PieChart,
    size: 'md',
    role: 'any',
    Render: StatusDistributionWidget,
  },
  source_distribution: {
    id: 'source_distribution',
    title: 'Source distribution',
    description: 'How your leads are split across acquisition sources.',
    icon: PieChart,
    size: 'md',
    role: 'any',
    Render: SourceDistributionWidget,
  },
  recent_activities_full: {
    id: 'recent_activities_full',
    title: 'Recent activities (full)',
    description: 'Detailed feed with type-coloured icons and timestamps. 10 most recent.',
    icon: Activity,
    size: 'lg',
    role: 'any',
    Render: RecentActivitiesFullWidget,
  },
};

// Default sets reflect the layout that used to be hard-coded on Dashboard.js
// — Stats overview at the top, then My Tasks panel, Quick actions, source/
// status distributions, and Recent activities. Operators can rearrange
// or hide any of these; they're just sensible starting points per role.
export const DEFAULT_WIDGETS_USER = [
  'stats_overview',
  'my_tasks_full',
  'quick_actions',
  'my_open_tasks',
  'due_today',
  'overdue_tasks',
  'new_leads_week',
  'status_distribution',
  'source_distribution',
  'recent_activities_full',
  'upcoming_meetings',
];

export const DEFAULT_WIDGETS_ADMIN = [
  'stats_overview',
  'my_tasks_full',
  'quick_actions',
  'new_leads_week',
  'won_month',
  'pipeline_value',
  'overdue_tasks',
  'top_rep',
  'status_distribution',
  'source_distribution',
  'conversion_funnel',
  'recent_activities_full',
];
